import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Select, message } from 'antd';
import { Routes, Route, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getSearches } from '../../services/api';
import { Search } from '../../types';
import OpportunitiesTab from './components/OpportunitiesTab';
import RejectedTab from './components/RejectedTab';
import FilteredTab from './components/FilteredTab';
import AutoApplyOfferCard from '../../components/autoApply/AutoApplyOfferCard';
import { useAutoApplyOffer } from '../../hooks/useAutoApplyOffer';
import WidenSearchStrip from '../../components/searchPreferences/WidenSearchStrip';
import { useNotifications } from '../../contexts/NotificationContext';
import { useUserSettings } from '../../contexts/UserSettingsContext';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { hasRoomToWiden } from '../../utils/searchPreferences';
import css from './Opportunities.module.scss';

// Below this lifetime yield the net reads as too tight rather than merely
// worked through.
const THIN_YIELD = 5;

// A search needs to have been through at least one daily sweep before its
// yield says anything about the NET. Since first runs began spending the same
// per-user budget, only the first search created in a day reveals a full
// batch — the rest fall back to a floor of min(3, cap/2). So a five-search
// pack leaves four searches sitting at ~3 on day one for reasons that have
// nothing to do with the user's filters, and diagnosing that as "your net is
// too tight" sends them to loosen settings that were never the problem.
// One sweep is enough: from day two round 1 gives every search with anything
// to show a slot, so a genuinely thin search stays thin and a throttled one
// recovers.
const MIN_AGE_TO_DIAGNOSE_MS = 24 * 60 * 60 * 1000;

type TabKey = 'opportunities' | 'rejected' | 'filtered';

const tabFromPath = (pathname: string): TabKey => {
  // pathname under /opportunities/* — second segment is tab or id
  const parts = pathname.split('/').filter(Boolean);
  // ['opportunities'] → regular tab; ['opportunities', 'filtered', ...] → filtered; ['opportunities', 'rejected', ...] → rejected; otherwise regular
  if (parts[1] === 'filtered') return 'filtered';
  if (parts[1] === 'rejected') return 'rejected';
  return 'opportunities';
};

const Opportunities: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searches, setSearches] = useState<Search[]>([]);
  const [loadingSearches, setLoadingSearches] = useState(true);
  const { subscribe } = useNotifications();

  const activeTab = tabFromPath(location.pathname);
  const selectedSearchId = searchParams.get('searchId') || undefined;
  const [rejectedRefreshKey, setRejectedRefreshKey] = useState(0);
  const [opportunitiesRefreshKey, setOpportunitiesRefreshKey] = useState(0);
  // Unread pending opportunities, reported by OpportunitiesTab from the list
  // envelope (kept fresh by its optimistic mark-viewed decrement).
  const [pendingUnreadCount, setPendingUnreadCount] = useState(0);
  // Auto-Apply offer: the hook owns settings, dismissal and the "actually off"
  // check; this page adds only its own earned-the-offer condition below.
  const autoApplyOffer = useAutoApplyOffer('opportunities');
  const [pendingTotal, setPendingTotal] = useState(0);
  // Widen-search strip: separate dismissal, session-only — it re-offers on a
  // later visit if the list is still thin, which is when it's worth asking again.
  const { settings: userSettings } = useUserSettings();
  const [widenDismissed, setWidenDismissed] = useState(false);
  const [widenApplied, setWidenApplied] = useState(false);

  const handleUnreadCountChange = useCallback((count: number) => {
    setPendingUnreadCount(count);
  }, []);

  const handleTotalCountChange = useCallback((total: number) => {
    setPendingTotal(total);
  }, []);

  const refreshRejectedTab = useCallback(() => {
    setRejectedRefreshKey(k => k + 1);
  }, []);

  const refreshOpportunitiesTab = useCallback(() => {
    setOpportunitiesRefreshKey(k => k + 1);
  }, []);

  const fetchSearches = async () => {
    try {
      const response = await getSearches();
      setSearches(response.searches);
    } catch (error) {
      console.error('Failed to load searches:', error);
      message.error(getApiErrorMessage(error));
    } finally {
      setLoadingSearches(false);
    }
  };

  useEffect(() => {
    fetchSearches();
  }, []);

  useEffect(() => {
    return subscribe(
      ['opportunities_ready'],
      () => {
        fetchSearches();
        refreshOpportunitiesTab();
      }
    );
    // `refreshOpportunitiesTab` is listed rather than suppressed because doing
    // so costs nothing: it is a useCallback with an empty dep list around a
    // functional setState, so its identity is fixed for the life of the page
    // and this subscription is still torn down and rebuilt only when
    // `subscribe` changes.
  }, [subscribe, refreshOpportunitiesTab]);

  const handleTabChange = (key: string) => {
    // Switching tabs preserves searchId filter but resets pagination,
    // since each tab has independent pagination semantics.
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    params.delete('pageSize');
    const qs = params.toString();
    const target = key === 'opportunities' ? '/opportunities' : `/opportunities/${key}`;
    navigate(qs ? `${target}?${qs}` : target);
  };

  const handleSearchFilterChange = (value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('searchId', value);
    } else {
      params.delete('searchId');
    }
    const qs = params.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname);
  };

  const selectedSearch = searches.find(s => s.id === selectedSearchId);

  // Scope every widen-strip signal to what the filter is actually showing.
  const visibleSearches = selectedSearchId
    ? searches.filter(s => s.id === selectedSearchId)
    : searches;

  // Lifetime yield, NOT pendingTotal: pending counts opportunities awaiting
  // review, so someone who worked through a healthy queue sits at zero and
  // their net is fine. What's diagnostic is how little a search ever surfaced.
  const lifetimeYield = visibleSearches.reduce(
    (sum, s) => sum + (s.stats?.totalOpportunities ?? 0),
    0
  );

  // A search that has never run, or is mid-crawl, hasn't produced a thin list —
  // it hasn't finished producing one. SearchStatus has no terminal state and
  // `pipeline` is optional, so this falls back to the status check alone.
  // Age gates it too: see MIN_AGE_TO_DIAGNOSE_MS. A missing/unparseable
  // createdAt is treated as too young — better to withhold the offer than to
  // make a confident wrong diagnosis.
  const now = Date.now();
  const searchesSettled = visibleSearches.every(s => {
    const createdAt = s.createdAt ? Date.parse(s.createdAt) : NaN;
    const oldEnough =
      Number.isFinite(createdAt) && now - createdAt >= MIN_AGE_TO_DIAGNOSE_MS;
    return (
      oldEnough &&
      s.status !== 'pending' &&
      !s.pipeline?.stages?.some(stage => stage.state === 'active')
    );
  });

  const widenEligible =
    !loadingSearches &&
    visibleSearches.length > 0 &&
    searchesSettled &&
    lifetimeYield < THIN_YIELD &&
    userSettings !== null &&
    // Nothing honest left to offer once both levers are at their widest.
    hasRoomToWiden(userSettings);

  // A successful widen consumes the room that made the strip eligible, so the
  // gate above would unmount it the instant it saved. Keep it up to show its
  // own confirmation until the user dismisses it.
  const showWidenStrip =
    activeTab === 'opportunities' &&
    !widenDismissed &&
    userSettings !== null &&
    (widenEligible || widenApplied);

  // One offer at a time. The widen strip wins when both qualify: with a list
  // this thin, "Auto-Apply sends your best matches" has almost nothing to send.
  const showAutoApplyOffer =
    activeTab === 'opportunities' &&
    pendingTotal > 0 &&
    autoApplyOffer.visible &&
    !showWidenStrip;

  const tabItems = [
    {
      key: 'opportunities',
      // Email-inbox badge: "Pending (3 new)"; plain "Pending" when all read.
      label: pendingUnreadCount > 0 ? `Pending (${pendingUnreadCount} new)` : 'Pending',
    },
    { key: 'rejected', label: 'Rejected' },
    { key: 'filtered', label: 'Filtered' },
  ];

  return (
    <div className={css.opportunities}>
      <div className={css.header}>
        <h1>Opportunities</h1>
        <div className={css.filter}>
          <Select
            placeholder="All Searches"
            allowClear
            style={{ width: 250 }}
            value={selectedSearchId}
            onChange={handleSearchFilterChange}
            loading={loadingSearches}
            options={searches.map(s => ({
              value: s.id,
              label: `${s.role} in ${s.location}`,
            }))}
          />
          {selectedSearch && (
            <span className={css.filterLabel}>
              Showing: {selectedSearch.role} in {selectedSearch.location}
            </span>
          )}
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        className={css.tabs}
      />

      {/* Point-of-need settings discovery, Pending tab only (Rejected/Filtered
          stay clean). Both are gated above; at most one ever renders. */}
      {showWidenStrip && userSettings && (
        <WidenSearchStrip
          settings={userSettings}
          lifetimeYield={lifetimeYield}
          onWidened={() => setWidenApplied(true)}
          onDismiss={() => setWidenDismissed(true)}
        />
      )}

      {showAutoApplyOffer && autoApplyOffer.settings && (
        <AutoApplyOfferCard
          surface="opportunities"
          settings={autoApplyOffer.settings}
          onSaved={autoApplyOffer.onSaved}
          onDismiss={autoApplyOffer.dismiss}
        />
      )}

      <Routes>
        <Route
          index
          element={
            <OpportunitiesTab
              searchId={selectedSearchId}
              refreshKey={opportunitiesRefreshKey}
              onReject={refreshRejectedTab}
              onUnreadCountChange={handleUnreadCountChange}
              onTotalCountChange={handleTotalCountChange}
            />
          }
        />
        <Route
          path=":id"
          element={
            <OpportunitiesTab
              searchId={selectedSearchId}
              refreshKey={opportunitiesRefreshKey}
              onReject={refreshRejectedTab}
              onUnreadCountChange={handleUnreadCountChange}
              onTotalCountChange={handleTotalCountChange}
            />
          }
        />
        <Route
          path="rejected"
          element={
            <RejectedTab
              searchId={selectedSearchId}
              refreshKey={rejectedRefreshKey}
              onUndo={refreshOpportunitiesTab}
            />
          }
        />
        <Route
          path="rejected/:id"
          element={
            <RejectedTab
              searchId={selectedSearchId}
              refreshKey={rejectedRefreshKey}
              onUndo={refreshOpportunitiesTab}
            />
          }
        />
        <Route path="filtered" element={<FilteredTab searchId={selectedSearchId} />} />
        <Route path="filtered/:id" element={<FilteredTab searchId={selectedSearchId} />} />
      </Routes>
    </div>
  );
};

export default Opportunities;
